<template>
  <f7-page
    class="main-frm-repair-parts-finish-report"
    :page-content="false"
  >
    <template #fixed>
      <TheNavbar
        nav-title="Parte de reparación"
        :back-btn-enable="true"
        class="at-navbar secondary-navbar"
      />
    </template>

    <f7-page-content class="display-flex flex-direction-column">
      <!-- PDF -->
      <div class="pdf-wrapper pdf-wrapper__full-height">
        <VuePdfApp
          v-if="displayPdfComponent"
          :pdf="pdf"
        />
      </div>

      <!-- Footer Buttons -->
      <div class="footer-wrapper">
        <!-- Finish and create delivery note -->
        <f7-button
          fill
          color="green"
          @click="onFinishClick"
        >
          <UCSvgIcon
            name="fa6-solid-circle-check"
            width="16px"
            height="16px"
          />
          <span class="margin-left-half">Finalizar y crear albarán</span>
        </f7-button>
      </div>
    </f7-page-content>
  </f7-page>
</template>

<script setup>
// @ts-check

import { nextTick, onMounted, ref } from 'vue';

import { ConfirmDialogGH, HiDialog, HiNotification } from '@/js/common/HINotificationSupport';
import { SignTypes } from '@/js/enums/externalRepairParts';
import { callFinishPart, callGetRepairsReport } from '@/js/services/repairs/ApiRepairs';
import { f7 } from 'framework7-vue';
import { logError } from '@/js/utils/log';
import { convertBase64ToArrayBuffer } from '@/js/utils/convertBase64ToArrayBuffer';

import TheNavbar from '@/components/TheNavbar.vue';
import VuePdfApp from 'vue3-pdf-app';
import 'vue3-pdf-app/dist/icons/main.css';
import UCSvgIcon from '@/components/common/UCSvgIcon.vue';

/** @import { Ref } from 'vue' */

const props = defineProps({
  /** Stores the id of the selected work part */
  workPartId: { type: Number, required: true },
});

/** Stores whether the pdf component should be displayed */
const displayPdfComponent = ref(true);

/** @type {Ref<string | ArrayBuffer | undefined>} */
const pdf = ref(undefined);

/** A method that is called to generate the repairs report */
async function getRepairsReport() {
  HiDialog.Preloader(true, 'Imprimiendo el parte de reparación...');

  try {
    const { data } = await callGetRepairsReport(props.workPartId);

    if (data.Valor === true) {
      pdf.value = convertBase64ToArrayBuffer(data.Mensaje ?? '');
    } else {
      HiNotification.Toast(data.Mensaje || 'No se ha podido imprimir el parte de reparación');
    }
  } catch (error) {
    logError(error);
    HiNotification.Toast('No se ha podido imprimir el parte de reparación');
  } finally {
    HiDialog.Preloader(false);
  }
}

/**
 * A method that is called to open the page to sign the delivery note.
 * @param {number} workPartId - The ID of the work part.
 */
function navigateToRepairsDeliveryNote(workPartId) {
  f7.views.main.router.navigate('/repairs/repairParts/sign', {
    props: {
      workPartId,
      signType: SignTypes.DELIVERY_NOTE,
      canGoBack: false,
    },
  });
}

/** A method that is called to open the work parts list if a signature is not required */
function navigateToWorkParts() {
  f7.views.main.router.clearPreviousHistory();
  f7.views.main.router.navigate('/repairs/repairParts', { force: true });
}

/**
 * A method that is used to finish part (type: Order and Part).
 * @param {boolean} valued - Whether the part is valued.
 */
async function finishPart(valued) {
  HiDialog.Preloader(true, 'Finalizando el parte...');

  try {
    const { data } = await callFinishPart(
      props.workPartId,
      valued,
    );

    if (data.Valor === true) {
      // The signature is required when there is LineaProduccionPorParte = Direccion
      if (data.RequiereFirmaCliente) {
        // Need to hide the Vue component because otherwise in the new form the pdf won't be displayed
        displayPdfComponent.value = false;
        await nextTick();

        navigateToRepairsDeliveryNote(props.workPartId);
      } else {
        navigateToWorkParts();
      }
    } else {
      HiNotification.Toast(data.Mensaje || 'No se ha podido finalizar el parte');
    }
  } catch (error) {
    logError(error);
    HiNotification.Toast('No se ha podido finalizar el parte');
  } finally {
    HiDialog.Preloader(false);
  }
}

/** A method that is called when user clicks on 'Finish and Create Delivery Note' */
async function onFinishClick() {
  const warehouseValuePromise = new Promise((resolve) => {
    const warehouseValueDialog = f7.dialog.create({
      title: 'Valoración de consumo',
      text: 'Seleccione si se van a generar los consumos valorados o sin valorar',
      buttons: [
        {
          text: 'VALORADOS',
          onClick() {
            resolve(true);
          },
        },
        {
          text: 'NO VALORADOS',
          onClick() {
            resolve(false);
          },
        },
      ],
    });

    warehouseValueDialog.open();
  });

  const valued = await warehouseValuePromise;

  const resultConfirm = await ConfirmDialogGH(
    'Esta acción finalizará el parte y creará un albarán',
    '¿Quiere continuar?',
  );

  if (resultConfirm) {
    finishPart(valued);
  }
}

onMounted(() => {
  getRepairsReport();
});
</script>

<style scoped lang="scss">
.main-frm-repair-parts-finish-report {
  --date-picker-input-height: 38px;
  --date-picker-label-color: var(--f7-block-color);
  --date-picker-label-font-size: var(--f7-block-font-size);
  --date-picker-label-line-height: var(--f7-line-height);
  --date-picker-label-margin: calc(var(--f7-typography-margin) / 2) 0 0 calc(var(--f7-typography-margin) / 2);

  .pdf-wrapper {
    height: 50%;

    // If there are no buttons, pdf will have 100% of height
    &__full-height {
      height: 100%;
    }
  }

  .footer-wrapper {
    display: flex;
    flex-wrap: wrap;
    margin-top: auto;

    .button {
      height: 50px;
      flex: 1;
      min-width: 300px;
      border-radius: 0;
    }
  }
}
</style>
